import React, { useState } from 'react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Box,
  Tabs,
  Tab,
  Container,
  Paper,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Payments as PredictionsIcon,
  Assessment as AssessmentIcon,
  Analytics as AnalyticsIcon,
  Info as InfoIcon,
  ModelTraining as ModelManagementIcon,
  Build as TrainingIcon,
  Storage as DataIcon,
  Autorenew as RefinanceIcon,
  Science as ExpertIcon,
} from '@mui/icons-material';

import { TabPanelProps } from './types';
import LoanDefaultTab from './components/LoanDefaultTab';
import ScorecardTab from './components/ScorecardTab';
import UnifiedCreditAnalysis from './components/UnifiedCreditAnalysis';
import RefinanceTab from './components/RefinanceTab';
import AnalysisTab from './components/AnalysisTab';
import ModelInfoTab from './components/ModelInfoTab';
import ModelManagement from './components/ModelManagement';
import TrainingManagement from './components/TrainingManagement';
import DatasetManagement from './components/DatasetManagement';
import ExpertTab from './components/ExpertTab';

// Create Material-UI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 500,
    },
  },
});

// TabPanel component for tab content
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`credit-risk-tabpanel-${index}`}
      aria-labelledby={`credit-risk-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Tab accessibility props
function a11yProps(index: number) {
  return {
    id: `credit-risk-tab-${index}`,
    'aria-controls': `credit-risk-tabpanel-${index}`,
  };
}

function App() {
  const [tabValue, setTabValue] = useState(0);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCloseNotification = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification({ ...notification, open: false });
  };

  const showNotification = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
        {/* App Bar */}
        <AppBar position="static" elevation={0}>
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              SUMA Credit Risk ML Platform
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Advanced Credit Risk Assessment System
            </Typography>
          </Toolbar>
        </AppBar>

        {/* Navigation Tabs */}
        <Paper elevation={1} sx={{ borderRadius: 0 }}>
          <Container maxWidth="xl">
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="credit risk platform tabs"
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Tab
                icon={<AssessmentIcon />}
                iconPosition="start"
                label="Credit Analysis"
                {...a11yProps(0)}
                sx={{ minHeight: 64, textTransform: 'none' }}
              />
              <Tab
                icon={<AnalyticsIcon />}
                iconPosition="start"
                label="Data Analysis"
                {...a11yProps(1)}
                sx={{ minHeight: 64, textTransform: 'none' }}
              />
              <Tab
                icon={<InfoIcon />}
                iconPosition="start"
                label="Model Performance"
                {...a11yProps(2)}
                sx={{ minHeight: 64, textTransform: 'none' }}
              />
              <Tab
                icon={<ModelManagementIcon />}
                iconPosition="start"
                label="Gestión de Modelos"
                {...a11yProps(3)}
                sx={{ minHeight: 64, textTransform: 'none' }}
              />
              <Tab
                icon={<TrainingIcon />}
                iconPosition="start"
                label="Entrenamiento"
                {...a11yProps(4)}
                sx={{ minHeight: 64, textTransform: 'none' }}
              />
              <Tab
                icon={<DataIcon />}
                iconPosition="start"
                label="Gestión de Datos"
                {...a11yProps(5)}
                sx={{ minHeight: 64, textTransform: 'none' }}
              />
              <Tab
                icon={<RefinanceIcon />}
                iconPosition="start"
                label="Refinanciamiento"
                {...a11yProps(6)}
                sx={{ minHeight: 64, textTransform: 'none' }}
              />
              <Tab
                icon={<ExpertIcon />}
                iconPosition="start"
                label="Experto"
                {...a11yProps(7)}
                sx={{ minHeight: 64, textTransform: 'none' }}
              />
            </Tabs>
          </Container>
        </Paper>

        {/* Tab Content */}
        <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
          <TabPanel value={tabValue} index={0}>
            <UnifiedCreditAnalysis onNotification={showNotification} />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <AnalysisTab onNotification={showNotification} />
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <ModelInfoTab onNotification={showNotification} />
          </TabPanel>
          <TabPanel value={tabValue} index={3}>
            <ModelManagement onNotification={showNotification} />
          </TabPanel>
          <TabPanel value={tabValue} index={4}>
            <TrainingManagement onNotification={showNotification} />
          </TabPanel>
          <TabPanel value={tabValue} index={5}>
            <DatasetManagement onNotification={showNotification} />
          </TabPanel>
          <TabPanel value={tabValue} index={6}>
            <RefinanceTab onNotification={showNotification} />
          </TabPanel>
          <TabPanel value={tabValue} index={7}>
            <ExpertTab onNotification={showNotification} />
          </TabPanel>
        </Container>

        {/* Notification Snackbar */}
        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseNotification}
            severity={notification.severity}
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}

export default App;
